import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';

interface PrintJobStatusCellProps {
  state: 'Scheduled' | 'Progress' | 'Completed' | 'Failed' | 'NO-PRINT';
  onRetry?: () => void;
  showRetryButton?: boolean;
}

const PrintJobStatusCell: FC<PrintJobStatusCellProps> = ({ 
  state, 
  onRetry, 
  showRetryButton = true 
}) => {
  const { t } = useTranslation();
  // FIXME: unable to determine the print state of the check item in the current form
  // - we are merging all states from all the check item related prints into/onto the check item print collector
  // - this places the check item state in a failed state when there are any failed states for the check item
  if (state === 'Scheduled') {
    return (
      <>
        <FontIcon name="schedule" color="warning"/>
        <Copy value={t('labels.scheduled')} color="warning"/>
      </>
    );
  }
  
  if (state === 'Progress') {
    return (
      <>
        <FontIcon name="schedule" color="warning"/>
        <Copy value={t('labels.processing')} color="warning"/>
      </>
    );
  }
  
  if (state === 'Completed') {
    // If a backup printer was configured, and it did not print to the primary, but printed to the backup,
    // the print job notification will look like this, and it’s implied that printing to the primary printer failed
    // (since printedToBackup is true and there is no error string):
    // Also, as far as determining the reason for the failure on the primary (if you should ever need to display it),
    // it's hard to determine with a high level of precision, as we could temporarily lose communication with it for several reasons.
    // As the best guess, you could just use the current printer status.
    // If it's "ready" and still fails on primary, it's probably just busy printing other things.
    return (
      <Copy value={t('labels.printed')}/>
    );
  }
  
  if (state === 'Failed') {
    if (showRetryButton && onRetry) {
      return (
        <CTA
          color="primary"
          label={t('labels.retry')}
          onClick={onRetry}
        />
      );
    }   
    return (
      <>
        <FontIcon name="warning" color="error"/>
        <Copy value={t('labels.printFailed')} color="error"/>
      </>
    );
  }
  if (state === 'NO-PRINT') {
    return (
      <Copy value={t('printer.labels.noPrint')}/>
    );
  }
  
  return <Copy value={t('printer.labels.unknownPrintState')} />;
};

export { PrintJobStatusCell };
